import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SessionService } from "@services/session.service";
import { AlertService } from "@services/alert.service";
import { PrestadorService } from "@services/prestador.service";
import { ExpedienteService } from "@services/expediente.service";
import { LicenciaService } from "@services/licencia.service";
import { ErrorService } from "@services/error.service";
import { Prestador } from "@models/prestador.model";
import { DangerError, WarningError } from "@shared/errors/custom.error";
import { parseRut, validarRutMod11 } from "@shared/utils/rutParser.utils";
import inicializarDataTable from "@shared/helpers/datatables.helper";
import { BaseFuncionalidadComponent } from "@shared/base/base-funcionalidad.component";
import IRegion from "@models/region.interface";
import { RegionService } from "@services/region.service";
import { Location } from "@angular/common";
import { StateService } from "@services/state.service";
import { Licencia } from "@models/licencia.model";
import { isValidEmail } from "@shared/utils/validators.utils";
import { TerminologiaService } from "@services/terminologia.service";
import sprintf from "@shared/utils/sprintf.util";
import { Mensajes } from "@shared/enums/mensajes.enum";
import ICompin from "@models/compin.interface";
import { CompinService } from "@services/compin.service";
import { NotificacionService } from "@services/notificacion.service";

@Component({
	selector: "app-expedientes-crear",
	templateUrl: "./expedientes-crear.component.html",
	styleUrls: ["./expedientes-crear.component.scss"],
})
export class ExpedientesCrearComponent extends BaseFuncionalidadComponent {
	constructor(
		sessionService: SessionService,
		route: ActivatedRoute,
		router: Router,
		location: Location,
		stateService: StateService,
		private toastr: AlertService,
		private prestadorService: PrestadorService,
		private expedienteService: ExpedienteService,
		private licenciaService: LicenciaService,
		private compinService: CompinService,
		private errorService: ErrorService,
		private terminologiaService: TerminologiaService,
		private notificacionService: NotificacionService
	) {
		super(route, sessionService, location, router, stateService);
	}

	rutPrestador: string = ""; // Variable que maneja el rut ingresado en el input rut del prestador
	licenciasSeleccionadas: string[] = []; // Variable que maneja las licencias seleccionadas en la tabla
	showResultados: boolean = false; // Variable que maneja el renderizado de la información del prestador
	showResultadosLicencias: boolean = false; // Variable que maneja el renderizado de la tabla licencias del prestador
	correoPrestadorResultado: string = ""; // Variable que maneja el correo electrónico ingresado en el input correo prestador
	licenciasResultados: Licencia[] = []; // Variable que guarda la información de las licencias medicas que se muestran en la tabla
	datosPrestador: Prestador; // Variable que guarda la información del prestador
	isLoading: boolean = false; // Variable que maneja el renderizado del spinner de carga de la información del prestador
	isLoadingLicencias: boolean = false; // Variable que maneja el renderizado del spinner de carga de la tabla licencias medicas
	isLoadingCreate: boolean = false; // Variable que maneja el renderizado del spinner de carga del botón crear expediente
	isLoadingUpdate: boolean = false; // Variable que maneja el renderizado del spinner de carga del boton actualizar correo
	emailUpdate: boolean = false; // Variable que maneja el renderizado del input para actualizar el correo alternativo
	regionSeleccionada: number; // Variable que guarda el id de la región que se selecciona al buscar las licencias
	regiones: IRegion[]; // Variable que guarda las regiones
	mensajeExito: string; // variable que guarda el mensaje de exito en la acción del expediente
	mostrarModalCreacion = false; // Variable que maneja el renderizado del modal de creación de expediente

	mensajeSinResultados: string; // Mensaje de respuesta de la BD

	ngOnInit() {
		this.obtenerRegiones();
	}

	get mensajeSinPermisos(): string {
		return this.terminologiaService.obtenerMensajePorID(
			Mensajes.sin_permisos_funcionalidad
		);
	}

	get mensajeSinPermisosAccion(): string {
		return this.terminologiaService.obtenerMensajePorID(
			Mensajes.sin_permisos_accion
		);
	}

	get mensajeLicenciasSeleccionadas(): string {
		const mensaje = this.terminologiaService.obtenerMensajePorID(
			Mensajes.licencias_seleccionadas
		);
		return sprintf(mensaje, this.licenciasSeleccionadas.length);
	}

	get mensajeCreacionExpediente(): string {
		return this.terminologiaService.obtenerMensajePorID(
			Mensajes.crear_expediente
		);
	}

	async buscarPrestador() {
		this.isLoading = true;
		this.showResultados = false;
		this.licenciasResultados = [];
		try {
			if (!this.canSearch)
				throw new DangerError(this.mensajeSinPermisosAccion);
			if (!this.rutPrestador)
				throw new WarningError("Se debe ingresar un RUN válido.");
			if (!validarRutMod11(this.rutPrestador))
				throw new WarningError("El RUN no es válido.");

			const { rut } = parseRut(this.rutPrestador);
			this.datosPrestador =
				await this.prestadorService.obtenerInformacion(rut);
			this.correoPrestadorResultado =
				this.datosPrestador.email_fiscalizacion;
			this.showResultados = true;
		} catch (error) {
			this.errorService.handle(
				error,
				"Error al obtener la información del prestador."
			);
		} finally {
			this.isLoading = false;
		}
	}

	async buscarLicenciasRegion() {
		this.isLoadingLicencias = true;
		this.showResultadosLicencias = false;
		this.licenciasResultados = [];
		try {
			if (!this.canSearch)
				throw new WarningError(this.mensajeSinPermisosAccion);
			if (!this.rutPrestador)
				throw new WarningError("Se debe ingresar un RUN válido.");
			if (!this.regionSeleccionada)
				throw new WarningError("Se debe seleccionar una región.");
			if (!validarRutMod11(this.rutPrestador))
				throw new WarningError("El RUN no es válido.");
			const { rut } = parseRut(this.rutPrestador);
			this.licenciasResultados =
				await this.licenciaService.obtenerLicenciasPorPrestador(
					rut,
					this.regionSeleccionada
				);
			this.showResultadosLicencias = true;
			inicializarDataTable("#licenciasTable", [0], {
				page: 0,
				search: "",
				sort: '[5, "desc"]',
			});
		} catch (error) {
			if (error instanceof DangerError) {
				this.mensajeSinResultados = error.message;
				this.showResultadosLicencias = true;
			} else
				this.errorService.handle(
					error,
					"Error al obtener la información del prestador."
				);
		} finally {
			this.isLoadingLicencias = false;
		}
	}

	toggleSeleccion(numLicencia: string, event: Event) {
		const checked = (event.target as HTMLInputElement).checked;
		const index = this.licenciasSeleccionadas.indexOf(numLicencia);

		if (checked && index === -1) {
			this.licenciasSeleccionadas.push(numLicencia);
		} else if (!checked && index > -1) {
			this.licenciasSeleccionadas.splice(index, 1);
		}
	}

	async crearExpediente() {
		this.isLoadingCreate = true;
		try {
			if (!this.canCreate)
				throw new DangerError(this.mensajeSinPermisosAccion);

			if (
				this.correoPrestadorResultado &&
				!isValidEmail(this.correoPrestadorResultado)
			) {
				throw new WarningError(
					"Debe ingresar un correo electrónico válido."
				);
			}

			if (this.licenciasSeleccionadas.length === 0) {
				throw new WarningError(
					"Debe seleccionar al menos una licencia para crear un expediente."
				);
			}

			const datosPrestador = structuredClone(this.datosPrestador) as any;
			datosPrestador.digver = datosPrestador.dv;

			// Crear expediente (el servicio se encarga de notificar tipo 1)
			this.mensajeExito = await this.expedienteService.crearExpediente(
				this.licenciasSeleccionadas,
				datosPrestador,
				Number(this.regionSeleccionada),
				this.correoPrestadorResultado
			);

			this.mostrarModalCreacion = false;
			this.mostrarModalConfirmacion = true; // Encadena el modal de confirmación tras aceptar
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al crear el expediente."
			);
		} finally {
			this.isLoadingCreate = false;
		}
	}

	limpiarResultados() {
		try {
			if (!this.canCleanData)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.showResultados = false;
			this.showResultadosLicencias = false;
			this.datosPrestador = null;
			this.licenciasResultados = [];
			this.licenciasSeleccionadas = [];
			this.rutPrestador = "";
			this.correoPrestadorResultado = "";
			this.emailUpdate = false;
			this.regionSeleccionada = null;
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al limpiar la información."
			);
		}
	}

	async actualizarCorreo() {
		try {
			if (!this.canUpdateAdditionalEmail)
				throw new DangerError(this.mensajeSinPermisosAccion);
			if (
				this.correoPrestadorResultado &&
				!isValidEmail(this.correoPrestadorResultado)
			)
				throw new WarningError(
					"Debe ingresar un correo electrónico válido."
				);
			this.emailUpdate = false;
		} catch (error) {
			this.errorService.handle(
				error,
				"Se produjo un error al guardar el correo electrónico."
			);
		}
	}

	async noActualizarCorreo() {
		this.emailUpdate = false;
		this.correoPrestadorResultado = "";
	}

	obtenerRegiones() {
		try {
			if (!this.canSearch)
				throw new DangerError(this.mensajeSinPermisosAccion);
			this.regiones = this.terminologiaService.obtenerRegiones();
			if (this.regiones.length === 1)
				this.regionSeleccionada = this.regiones[0].codigo;
		} catch (error) {
			this.errorService.handle(error, "Error al obtener las regiones.");
		}
	}

	onModalConfirmacionCerrado() {
		this.mostrarModalConfirmacion = false;
		// Redirige al historial de expedientes al cerrar el modal de confirmación
		this.router.navigate(["/expedientes/historial"]);
	}
}
